import {
  AvailableDriveListResult,
  DriveAddInput,
  DriveBrowseFolderResult,
  DriveListResult,
  DriveLiveFindResult,
  DriveRemoveResult,
  DriveRootCreateBody,
  DriveRootRemoveResult,
  DriveRootSafe,
  DriveRootUpdateBody,
  DriveSafe,
  DriveSyncCurrentResult,
  DriveSyncRunListResult,
  DriveSyncStartResult,
  DriveAnchorDecisionsInput,
  DriveAnchorDecisionsResult,
  DriveAnchorProposalListResult,
  DriveAnchorProposalSafe,
  DriveApplyRootsResult,
  DriveKeepBody,
  DriveMirroredRefsBody,
  DriveMirroredRefsResult,
  DrivePinResult,
  DrivePublishRemoveResult,
  DrivePublishSetInput,
  DrivePublishStatusResult,
  DrivePublishTargetSafe,
  FileTextRead,
  KeepLiveFilesResult,
} from '@vivport/backend/types';
import { httpClient } from './http-client';

export async function listAvailableDrives(): Promise<AvailableDriveListResult> {
  return httpClient.get<AvailableDriveListResult>('/protected/drives/available');
}

export async function listDrives(): Promise<DriveListResult> {
  return httpClient.get<DriveListResult>('/protected/drives/');
}

export async function addDrive(input: DriveAddInput): Promise<DriveSafe> {
  return httpClient.post<DriveSafe, DriveAddInput>('/protected/drives/', input);
}

export async function removeDrive(driveId: number): Promise<DriveRemoveResult> {
  return httpClient.delete<DriveRemoveResult>(`/protected/drives/${driveId}`);
}

export async function browseDriveFolder(driveId: number, parent: string | null): Promise<DriveBrowseFolderResult> {
  const query = parent ? `?parent=${encodeURIComponent(parent)}` : '';
  return httpClient.get<DriveBrowseFolderResult>(`/protected/drives/${driveId}/browse${query}`);
}

export async function addDriveRoot(driveId: number, body: DriveRootCreateBody): Promise<DriveRootSafe> {
  return httpClient.post<DriveRootSafe, DriveRootCreateBody>(`/protected/drives/${driveId}/roots`, body);
}

export async function updateDriveRoot(rootId: number, body: DriveRootUpdateBody): Promise<DriveRootSafe> {
  return httpClient.patch<DriveRootSafe, DriveRootUpdateBody>(`/protected/drives/roots/${rootId}`, body);
}

export async function removeDriveRoot(rootId: number): Promise<DriveRootRemoveResult> {
  return httpClient.delete<DriveRootRemoveResult>(`/protected/drives/roots/${rootId}`);
}

export async function syncDriveNow(driveId: number): Promise<DriveSyncStartResult> {
  return httpClient.post<DriveSyncStartResult>(`/protected/drives/${driveId}/sync-now`);
}

export async function resyncDrive(driveId: number): Promise<DriveSyncStartResult> {
  return httpClient.post<DriveSyncStartResult>(`/protected/drives/${driveId}/resync`);
}

export async function listDriveSyncRuns(driveId: number): Promise<DriveSyncRunListResult> {
  return httpClient.get<DriveSyncRunListResult>(`/protected/drives/${driveId}/sync-runs`);
}

export async function getCurrentDriveSyncRun(driveId: number): Promise<DriveSyncCurrentResult> {
  return httpClient.get<DriveSyncCurrentResult>(`/protected/drives/${driveId}/sync-runs/current`);
}

export async function listAnchorProposals(rootId?: number): Promise<DriveAnchorProposalListResult> {
  const query = rootId !== undefined ? `?rootId=${rootId}` : '';
  return httpClient.get<DriveAnchorProposalListResult>(`/protected/drives/anchor-proposals${query}`);
}

export async function decideAnchorProposals(input: DriveAnchorDecisionsInput): Promise<DriveAnchorDecisionsResult> {
  return httpClient.post<DriveAnchorDecisionsResult, DriveAnchorDecisionsInput>('/protected/drives/anchor-proposals/decide', input);
}

export async function reopenAnchorProposal(proposalId: number): Promise<DriveAnchorProposalSafe> {
  return httpClient.post<DriveAnchorProposalSafe>(`/protected/drives/anchor-proposals/${proposalId}/reopen`);
}

export async function applyDriveRoots(driveId: number): Promise<DriveApplyRootsResult> {
  return httpClient.post<DriveApplyRootsResult>(`/protected/drives/${driveId}/apply-roots`);
}

export async function getPublishStatus(): Promise<DrivePublishStatusResult> {
  return httpClient.get<DrivePublishStatusResult>('/protected/drives/publish-target');
}

export async function setPublishTarget(input: DrivePublishSetInput): Promise<DrivePublishTargetSafe> {
  return httpClient.put<DrivePublishTargetSafe, DrivePublishSetInput>('/protected/drives/publish-target', input);
}

export async function removePublishTarget(): Promise<DrivePublishRemoveResult> {
  return httpClient.delete<DrivePublishRemoveResult>('/protected/drives/publish-target');
}

export async function publishNow(): Promise<DrivePublishTargetSafe> {
  return httpClient.post<DrivePublishTargetSafe>('/protected/drives/publish-target/publish-now');
}

export async function readFileText(
  contentId: number,
  params: { startChar?: number; maxChars?: number } = {}
): Promise<FileTextRead> {
  return httpClient.get<FileTextRead>(`/protected/content/${contentId}/file-text`, { params });
}

export async function pinReference(contentId: number): Promise<DrivePinResult> {
  return httpClient.post<DrivePinResult>('/protected/drives/pin', { contentId });
}

export async function keepDriveFiles(driveId: number, body: DriveKeepBody): Promise<KeepLiveFilesResult> {
  return httpClient.post<KeepLiveFilesResult>(`/protected/drives/${driveId}/live/keep`, body);
}

export async function listDriveMirroredRefs(
  driveId: number,
  body: DriveMirroredRefsBody
): Promise<DriveMirroredRefsResult> {
  return httpClient.post<DriveMirroredRefsResult>(`/protected/drives/${driveId}/live/mirrored-refs`, body);
}

export async function findLiveFiles(
  driveId: number,
  params: { query: string; limit?: number }
): Promise<DriveLiveFindResult> {
  return httpClient.get<DriveLiveFindResult>(`/protected/drives/${driveId}/live/find`, { params });
}
